import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

const ACCESS_TOKEN_KEY = "emlak_access_token";

export const getAccessToken = () => localStorage.getItem(ACCESS_TOKEN_KEY);
export const setAccessToken = (token) => {
  if (token) localStorage.setItem(ACCESS_TOKEN_KEY, token);
};
export const clearAccessToken = () => localStorage.removeItem(ACCESS_TOKEN_KEY);

export const http = axios.create({ baseURL: API, timeout: 20000 });

http.interceptors.request.use((cfg) => {
  const token = getAccessToken();
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// The API wraps every response as { data, success, message } (IDataResult).
// Endpoints that only return IResult have { success, message } and no data.
// Logical failures (e.g. wrong password) come back as HTTP 200 with
// success:false, so surface those as thrown errors like a real HTTP error.
class ApiError extends Error {
  constructor(message) {
    super(message || "Xəta baş verdi.");
    this.name = "ApiError";
  }
}
const checkSuccess = (body) => {
  if (body && body.success === false) throw new ApiError(body.message);
  return body;
};
const unwrap = (res) => checkSuccess(res.data)?.data;
const raw = (res) => checkSuccess(res.data);

/* ------------------------------------------------------------------ */
/* Auth                                                                 */
/* ------------------------------------------------------------------ */
// POST /api/Auth/login — LoginRequestDto { phoneNumber, password } -> LoginResponseDto { user, accessToken, accessTokenExpireDate }
export const login = (phoneNumber, password) =>
  http.post("/Auth/login", { phoneNumber, password }).then(unwrap);

// GET /api/Auth/refresh/token -> LoginResponseDto
export const refreshAccessToken = () => http.get("/Auth/refresh/token").then(unwrap);

// GET /api/Auth/loginInfo -> LoginResponseDto (resolve current user from the access token)
export const fetchLoginInfo = () => http.get("/Auth/loginInfo").then(unwrap);

// POST /api/Auth/logout -> IResult
export const logout = () => http.post("/Auth/logout").then(raw);

/* ------------------------------------------------------------------ */
/* Helper (reference/combo data)                                       */
/* ------------------------------------------------------------------ */
// GET /api/Helper/combos -> ComboResponseDto
// Each list item is { value, label, data } (not { id, name }).
export const fetchCombos = () => http.get("/Helper/combos").then(unwrap);

/* ------------------------------------------------------------------ */
/* Propertys                                                            */
/* ------------------------------------------------------------------ */
// POST /api/Propertys/{pageIndex}/{pageSize} — body: ComboRequestDto
// NOTE: verified against the live API — this endpoint consistently returns
// data:null regardless of filters/pageIndex. Kept here for spec parity, but
// the working, verified listing endpoint is fetchPropertiesKeyset below.
export const fetchPropertiesPaged = (pageIndex, pageSize, comboRequest = {}) =>
  http.post(`/Propertys/${pageIndex}/${pageSize}`, comboRequest).then(unwrap);

// POST /api/Propertys/keyset/{pageSize}?lastId= — body: ComboRequestDto
// Verified live shape: { data: { data: PropertyResponseDto[], nextCursor }, success, message }
export const fetchPropertiesKeyset = (pageSize, lastId, comboRequest = {}) =>
  http
    .post(`/Propertys/keyset/${pageSize}`, comboRequest, { params: lastId ? { lastId } : {} })
    .then((r) => r.data?.data || { data: [], nextCursor: null });

// GET /api/Propertys -> PropertyResponseDto[] (unfiltered/full list — slow, avoid for UI)
export const fetchAllProperties = () => http.get("/Propertys").then(unwrap);

// POST /api/Propertys — PropertyCreateRequestDto -> IResult
export const createProperty = (dto) => http.post("/Propertys", dto).then(raw);

// GET /api/Propertys/{id} -> PropertyByIdResponseDto
export const fetchProperty = (id) => http.get(`/Propertys/${id}`).then(unwrap);

// PUT /api/Propertys/{id} — PropertyUpdateRequestDto -> IResult
export const updateProperty = (id, dto) => http.put(`/Propertys/${id}`, dto).then(raw);

export const fetchUserPackageStatus = () => http.get("/Users/package/status").then(unwrap);
// DELETE /api/Propertys/{id} -> IResult
export const deleteProperty = (id) => http.delete(`/Propertys/${id}`).then(raw);

// GET /api/Propertys/byCode/{propertyCode} -> PropertyByIdResponseDto
// NOTE: propertyCode is the GUID-style `propertyCode` field, NOT the numeric `code` field.
export const fetchPropertyByCode = (propertyCode) =>
  http.get(`/Propertys/byCode/${propertyCode}`).then(unwrap);

// PUT /api/Propertys/updatePropertyViewCount/{id}
export const updatePropertyViewCount = (id) =>
  http.put(`/Propertys/updatePropertyViewCount/${id}`).then(raw);

// GET /api/Propertys/getMonthlyPriceStats/{id} -> GetPropertyStatResponseDto[]
export const fetchMonthlyPriceStats = (id) =>
  http.get(`/Propertys/getMonthlyPriceStats/${id}`).then(unwrap);

// GET /api/Propertys/getNewPropertyNotification/{lastPropertyId}?isFirst= -> PropertyNotificationResponseDto
export const fetchNewPropertyNotification = (lastPropertyId, isFirst) =>
  http
    .get(`/Propertys/getNewPropertyNotification/${lastPropertyId}`, { params: { isFirst } })
    .then(unwrap);

// GET /api/Propertys/getPropertyViewHeatLevel -> PropertyViewHeatLevelDto { hot, popular, active }
export const fetchPropertyViewHeatLevel = () =>
  http.get("/Propertys/getPropertyViewHeatLevel").then(unwrap);

// POST /api/Propertys/download-all — body: string[] (property codes) -> FileContentResult
export const downloadAllProperties = (codes) =>
  http.post("/Propertys/download-all", codes).then(unwrap);

// PUT /api/Propertys/updatePropertyLinkVisitCount/{idProperty}
export const updatePropertyLinkVisitCount = (idProperty) =>
  http.put(`/Propertys/updatePropertyLinkVisitCount/${idProperty}`).then(unwrap);

// Favorites are modeled server-side as a "color" tag on the property
// (PropertyResponseDto.isTracked / .color reflect the current user's state).
export const FAVORITE_COLOR = "#F44336";

// POST /api/Propertys/color — PropertyColorCreateRequestDto { fkIdProperty, color } -> IResult
export const addPropertyColor = (fkIdProperty, color = FAVORITE_COLOR) =>
  http.post("/Propertys/color", { fkIdProperty, color }).then(raw);

// DELETE /api/Propertys/color/{idProperty} -> IResult
export const removePropertyColor = (idProperty) =>
  http.delete(`/Propertys/color/${idProperty}`).then(raw);

// PUT /api/Propertys/color/{idProperty}?color= -> IResult
export const changePropertyColor = (idProperty, color) =>
  http.put(`/Propertys/color/${idProperty}`, null, { params: { color } }).then(raw);

/* ------------------------------------------------------------------ */
/* Filters (saved searches)                                            */
/* ------------------------------------------------------------------ */
// POST /api/Filters/{pageIndex}/{pageSize} -> FilterResponseDto[] ({ value, label } per live response)
export const fetchFiltersPaged = (pageIndex, pageSize) =>
  http.post(`/Filters/${pageIndex}/${pageSize}`).then(unwrap);

// GET /api/Filters -> live shape: { value: idFilter, label: name, data: JSON string of ComboRequestDto }
export const fetchFilters = () => http.get("/Filters").then(unwrap);

// POST /api/Filters — FilterCreateRequestDto { data, name, fkIdUser } -> ComboDto
export const createFilter = (dto) => http.post("/Filters", dto).then(unwrap);

// GET /api/Filters/{id} -> FilterByIdResponseDto { idFilter, data }
export const fetchFilterById = (id) => http.get(`/Filters/${id}`).then(unwrap);

// DELETE /api/Filters/{id} -> IResult
export const deleteFilter = (id) => http.delete(`/Filters/${id}`).then(raw);

/* ------------------------------------------------------------------ */
/* Packages                                                             */
/* ------------------------------------------------------------------ */
// GET /api/Packages -> PackageDto[] { idPackage, name, periodDays, price }
export const fetchPackages = () => http.get("/Packages").then(unwrap);

/* ------------------------------------------------------------------ */
/* Feedback (property reports)                                         */
/* ------------------------------------------------------------------ */
// GET /api/FeedBackTypes/feedback-types -> FeedBackTypeResponseDto[] { idFeedbackType, feedbackTypeName, color, icon }
export const fetchFeedBackTypes = () => http.get("/FeedBackTypes/feedback-types").then(unwrap);

// POST /api/PropertyFeedBack — CreatePropertyFeedBackDto { fkIdProperty, fkIdFeedbackType } -> string
export const createPropertyFeedBack = (fkIdProperty, fkIdFeedbackType) =>
  http.post("/PropertyFeedBack", { fkIdProperty, fkIdFeedbackType }).then(unwrap);

/* ------------------------------------------------------------------ */
/* UserPayments                                                        */
/* ------------------------------------------------------------------ */
// GET /api/UserPayments/{pageIndex}/{pageSize}
// Live shape: { datas: UserPaymentResponseDto[], pageIndex, totalPageCount, totalRecordCount, hasPreviousPage, hasNextPage }
export const fetchUserPaymentsPaged = (pageIndex, pageSize) =>
  http.get(`/UserPayments/${pageIndex}/${pageSize}`).then(unwrap);

// POST /api/UserPayments/getUserDebt — UserPaymentDebtRequestDto { userCode, amount, authentication } -> IResult
export const fetchUserDebt = (dto) => http.post("/UserPayments/getUserDebt", dto).then(raw);

// POST /api/UserPayments/insertUserPayment — FnSetNewUserParameters { userCode, amount, transactionId, source, authentication } -> IResult
// NOTE: `authentication` is a payment-gateway signature; it cannot be produced
// safely from the browser. This is wired for parity but real checkout needs a
// server-side/PSP-redirect integration to compute it.
export const insertUserPayment = (dto) => http.post("/UserPayments/insertUserPayment", dto).then(raw);

/* ------------------------------------------------------------------ */
/* Users                                                                */
/* ------------------------------------------------------------------ */
// GET /api/Users/{pageIndex}/{pageSize} -> UserResponseDto[]
export const fetchUsersPaged = (pageIndex, pageSize) =>
  http.get(`/Users/${pageIndex}/${pageSize}`).then(unwrap);

// GET /api/Users/userReferanceStatus
// Live shape: { referralUserCount, referralUserPaymentCount, referralBonusDays }
export const fetchUserReferenceStatus = () => http.get("/Users/userReferanceStatus").then(unwrap);

// GET /api/Users -> UserResponseDto[]
export const fetchUsers = () => http.get("/Users").then(unwrap);

// POST /api/Users — UserCreateRequestDto { phoneNumber, referralId } -> IResult
export const createUser = (phoneNumber, referralId) =>
  http.post("/Users", { phoneNumber, referralId }).then(raw);

// PUT /api/Users/viewMode/{mode} -> UserResponseDto[]
export const updateViewMode = (mode) => http.put(`/Users/viewMode/${mode}`).then(unwrap);

// GET /api/Users/profile -> UserResponseDto
export const fetchProfile = () => http.get("/Users/profile").then(unwrap);

// GET /api/Users/{id} -> UserResponseDto
export const fetchUser = (id) => http.get(`/Users/${id}`).then(unwrap);

// PUT /api/Users/{id} — UserUpdateRequestDto { contactNumber } -> IResult
export const updateUser = (id, dto) => http.put(`/Users/${id}`, dto).then(raw);

// DELETE /api/Users/{id} -> IResult
export const deleteUser = (id) => http.delete(`/Users/${id}`).then(raw);

// POST /api/Users/resetPassword — ResetPasswordRequestDto { password, passwordConfirmation } -> IResult
export const resetPassword = (password, passwordConfirmation) =>
  http.post("/Users/resetPassword", { password, passwordConfirmation }).then(raw);

// POST /api/Users/resetForgotPassword — ResetForgotPasswordRequestDto { phoneNumber } -> IResult
export const resetForgotPassword = (phoneNumber) =>
  http.post("/Users/resetForgotPassword", { phoneNumber }).then(raw);

// POST /api/Users/{id}/resetPassword — ResetPasswordRequestDto -> IResult
export const resetPasswordById = (id, password, passwordConfirmation) =>
  http.post(`/Users/${id}/resetPassword`, { password, passwordConfirmation }).then(raw);

// GET /api/Users/code -> the current user's own referral code (their idUser)
export const fetchUserCode = () => http.get("/Users/code").then(unwrap);

/* ------------------------------------------------------------------ */
/* View heat level (for the "Çox baxılanlar" / hot tab)                */
/* ------------------------------------------------------------------ */
const VIEW_HEAT_LEVEL_KEY = "emlak_view_heat_level";

export const getCachedViewHeatLevel = () => {
  try {
    return JSON.parse(localStorage.getItem(VIEW_HEAT_LEVEL_KEY) || "null");
  } catch {
    return null;
  }
};

export const cacheViewHeatLevel = (data) => {
  try {
    localStorage.setItem(VIEW_HEAT_LEVEL_KEY, JSON.stringify(data));
  } catch { }
};

/* ------------------------------------------------------------------ */
/* ComboRequestDto builder                                              */
/* ------------------------------------------------------------------ */
// UI filter state keeps single-select fields as plain string ids (simpler
// widgets); the API expects several of them as int[]. Multi-select fields
// (idPropertyType, regionIds, metroIds, roomIds, ...) store a comma-separated
// string like "1,2,3" — this converts the flat UI filter object into a
// proper ComboRequestDto body, splitting those comma-separated ids into a
// real int[].
const COMBO_ARRAY_KEYS = ["idPropertyType", "regionIds", "metroIds", "roomIds", "buildingTypeIds", "repairRateIds", "targetIds"];
const COMBO_INT_KEYS = ["idOwnerType", "idOperationType", "idDocument", "floorMin", "floorMax", "buildingfloorMin", "buildingfloorMax", "isOnlyExtract", "isOnlyLoan", "isNotLastFloor", "hot"];
const COMBO_NUM_KEYS = ["priceMin", "priceMax", "areaMin", "areaMax", "generalAreaMin", "generalAreaMax", "landAreaMin", "landAreaMax"];

// UI-side sortType keys -> backend ComboRequestDto.SortType values
// (backend ApplyFilters only understands: dateAsc, dateDesc, priceAsc,
// priceDesc, codeAsc, codeDesc — anything else silently falls through to
// the default "order by IdProperty desc" branch).
export const SORT_TYPE_MAP = {
  newest: "dateDesc",
  oldest: "dateAsc",
  price_asc: "priceAsc",
  price_desc: "priceDesc",
};

export const toComboRequest = (filters = {}, extra = {}) => {
  const body = {};
  for (const [k, v] of Object.entries(filters)) {
    if (v === "" || v == null) continue;
    if (COMBO_ARRAY_KEYS.includes(k)) {
      // v may be a single id ("5") or a comma-separated multi-select string ("1,2,3")
      const ids = String(v)
        .split(",")
        .map((s) => Number(s.trim()))
        .filter((n) => !Number.isNaN(n));
      if (ids.length) body[k] = ids;
    } else if (COMBO_INT_KEYS.includes(k)) body[k] = Number(v);
    else if (COMBO_NUM_KEYS.includes(k)) body[k] = Number(v);
    else body[k] = v; // strings: startDate, endDate, phoneNumber, keyword, code, tab
  }

  const mappedExtra = { ...extra };
  if (mappedExtra.sortType) {
    mappedExtra.sortType = SORT_TYPE_MAP[mappedExtra.sortType] || mappedExtra.sortType;
  }

  return { ...body, ...mappedExtra };
};